import { useState } from 'react'
import { bgrDirectRemove, bgrDecodeFile, bgrPsdToBlob, bgrCanvasToBlob } from '../../utils/bgr'

function BgRemoveTab() {
  const [files, setFiles] = useState([])
  const [tolerance, setTolerance] = useState(35)
  const [outputDirHandle, setOutputDirHandle] = useState(null)
  const [running, setRunning] = useState(false)
  const [skipExisting, setSkipExisting] = useState(true)
  const hasFSA = 'showDirectoryPicker' in window

  function onFilesChange(e) {
    const picked = Array.from(e.target.files)
      .filter(f => /\.(psd|png|jpe?g)$/i.test(f.name))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
    setFiles(picked.map((file, i) => ({
      id: i, name: file.name,
      stem: file.name.replace(/\.[^.]+$/, ''),
      file, status: 'queued', pct: 0, blobUrl: null, error: null,
    })))
  }

  async function pickOutputFolder() {
    try {
      const h = await window.showDirectoryPicker({ mode: 'readwrite' })
      setOutputDirHandle(h)
    } catch (e) {
      if (e.name !== 'AbortError') alert(`Failed: ${e.message}`)
    }
  }

  function patchFile(id, patch) {
    setFiles(prev => prev.map(f => f.id === id ? { ...f, ...patch } : f))
  }

  async function run() {
    setRunning(true)
    const { readPsd } = await import('ag-psd')

    let outputDir = null
    if (outputDirHandle) {
      outputDir = await outputDirHandle.getDirectoryHandle('output', { create: true })
    }

    for (const item of files) {
      const outName = `${item.stem}_NoBG.png`

      if (skipExisting && outputDir) {
        try { await outputDir.getFileHandle(outName); patchFile(item.id, { status: 'skipped', pct: 100 }); continue }
        catch { /* not exists, process */ }
      }

      try {
        patchFile(item.id, { status: 'decoding', pct: 10 })
        const source = await bgrDecodeFile(item.file, readPsd)

        patchFile(item.id, { status: 'segmenting', pct: 40 })
        const finalBlob = await bgrDirectRemove(source, tolerance)

        const blobUrl = URL.createObjectURL(finalBlob)
        if (outputDir) {
          patchFile(item.id, { status: 'writing', pct: 95 })
          const fh = await outputDir.getFileHandle(outName, { create: true })
          const w = await fh.createWritable()
          await w.write(finalBlob); await w.close()
          patchFile(item.id, { status: 'done', pct: 100, blobUrl })
        } else {
          patchFile(item.id, { status: 'done', pct: 100, blobUrl, outName })
        }
      } catch (e) {
        console.error(item.name, e)
        patchFile(item.id, { status: 'error', pct: 100, error: (e.message || 'Error').slice(0, 60) })
      }
    }
    setRunning(false)
  }

  const counts = files.reduce((a, f) => { a[f.status] = (a[f.status] || 0) + 1; return a }, {})
  const doneCount = (counts.done || 0) + (counts.skipped || 0)

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-wrap items-center gap-3">
        <label className="btn-primary cursor-pointer text-sm px-4 py-2">
          Pick folder…
          <input type="file" className="hidden" accept=".psd,.png,.jpg,.jpeg"
            multiple webkitdirectory="" onChange={onFilesChange} disabled={running} />
        </label>
        {files.length > 0 && (
          <span className="text-sm text-gray-500 font-mono">
            {files.length} file{files.length > 1 ? 's' : ''}
          </span>
        )}

        <div className="w-px h-5 bg-gray-200" />

        {hasFSA ? (
          <button onClick={pickOutputFolder} disabled={running} className="btn-secondary text-sm px-3 py-2">
            {outputDirHandle ? `Output: ${outputDirHandle.name}` : 'Output folder…'}
          </button>
        ) : (
          <span className="text-xs text-gray-400">Output: download links (use Chrome/Brave for folder save)</span>
        )}

        <div className="w-px h-5 bg-gray-200" />

        {outputDirHandle && (
          <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
            <input type="checkbox" checked={skipExisting} onChange={e => setSkipExisting(e.target.checked)} disabled={running} />
            Skip existing
          </label>
        )}

        <label className="flex items-center gap-1.5 text-sm text-gray-600" title="Color tolerance for background detection. Lower = stricter (use 20–30 for clean studio renders). Higher = catches more background but risks eating into subject edges (use 40–55 for noisy or gradient backgrounds).">
          Tolerance
          <input
            type="number" min={10} max={80} step={5}
            value={tolerance}
            onChange={e => setTolerance(Math.min(80, Math.max(10, Number(e.target.value))))}
            disabled={running}
            className="w-14 border border-gray-300 rounded px-1 py-0.5 text-sm text-center"
          />
        </label>

        <div className="ml-auto">
          <button onClick={run} disabled={running || files.length === 0} className="btn-primary text-sm px-4 py-2">
            {running ? 'Processing…' : 'Start'}
          </button>
        </div>
      </div>

      {/* Summary */}
      {files.length > 0 && (
        <p className="text-sm text-gray-500">
          {running
            ? `Processing… ${doneCount} / ${files.length}`
            : doneCount === files.length
            ? `Done — ${counts.done || 0} saved, ${counts.skipped || 0} skipped, ${counts.error || 0} failed`
            : `${files.length} files ready`}
        </p>
      )}

      {/* File rows */}
      <div className="space-y-1.5">
        {files.map(item => (
          <BgrFileRow key={item.id} item={item} hasOutputDir={!!outputDirHandle} />
        ))}
      </div>

      {files.length === 0 && (
        <p className="text-center text-sm text-gray-400 py-8">
          Pick a folder of PSD, PNG, or JPG renders. Background is removed in your browser — no files are uploaded to any server.
        </p>
      )}
    </div>
  )
}

function BgrFileRow({ item, hasOutputDir }) {
  const statusColor = { queued: 'text-gray-400', decoding: 'text-blue-500', segmenting: 'text-blue-600',
    writing: 'text-blue-500', done: 'text-green-600', skipped: 'text-gray-400', error: 'text-red-500' }
  const statusLabel = { queued: 'queued', decoding: 'decoding…', segmenting: 'segmenting…',
    writing: 'writing…', done: hasOutputDir ? 'saved' : 'done', skipped: 'skipped', error: item.error || 'error' }
  const barColor = item.status === 'done' ? '#22c55e' : item.status === 'error' ? '#ef4444' : '#3b82f6'

  return (
    <div className="bg-white border border-gray-100 rounded-lg px-3 py-2 flex items-center gap-3">
      <div className="w-12 h-9 rounded flex-shrink-0 overflow-hidden border border-gray-100"
        style={{ background: 'repeating-conic-gradient(#e5e7eb 0% 25%, #fff 0% 50%) 0 0 / 12px 12px' }}>
        {item.blobUrl && <img src={item.blobUrl} alt="" className="w-full h-full object-contain" />}
      </div>
      <span className="font-mono text-xs text-gray-700 flex-1 truncate" title={item.name}>{item.name}</span>
      <div className="w-32 h-1.5 rounded-full bg-gray-100 flex-shrink-0">
        <div className="h-full rounded-full transition-all duration-100"
          style={{ width: `${item.pct}%`, backgroundColor: barColor }} />
      </div>
      <div className="w-28 text-right flex-shrink-0">
        {item.status === 'done' && item.blobUrl && !hasOutputDir ? (
          <a href={item.blobUrl} download={item.outName}
            className="text-xs font-semibold text-green-600 hover:underline">download</a>
        ) : (
          <span className={`text-xs ${statusColor[item.status] || 'text-gray-400'}`}>
            {statusLabel[item.status]}
          </span>
        )}
      </div>
    </div>
  )
}

export default BgRemoveTab
